import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Star, Clock, Utensils } from 'lucide-react';

export const Boston: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Best Chinese Food in Boston | Chinese Food Maps</title>
        <meta name="description" content="Discover the best Chinese restaurants in Boston. From authentic dim sum in Chinatown to regional specialties across the city, explore Boston's diverse Chinese food scene." />
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6 text-red-900">Best Chinese Food in Boston</h1>

        <div className="prose max-w-none text-gray-600 mb-12">
          <p className="text-lg leading-relaxed">
            Boston's historic Chinatown, centered around Beach Street and Tyler Street, is the third largest Chinese neighborhood in the United States. Known for its authentic dim sum houses, seafood restaurants, and regional specialties, Boston's Chinese food scene offers a rich tapestry of flavors from across China.
          </p>
        </div>

        <h2 className="text-2xl font-bold mb-8 text-black">Top Rated Chinese Food Restaurants</h2>

        <div className="grid gap-8">
          <div className="bg-white rounded-lg shadow-md p-8">
            <h2 className="text-2xl font-semibold text-red-800 mb-6">Boston Chinatown</h2>
            
            <div className="space-y-8">
              {/* China Pearl */}
              <div className="border-b border-gray-200 pb-8">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-semibold">China Pearl</h3>
                  <div className="flex items-center bg-yellow-100 px-3 py-1.5 rounded-full">
                    <Star size={18} className="text-yellow-500 mr-1" />
                    <span className="font-medium">4.3</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-3">
                  <MapPin size={18} className="text-red-600" />
                  <p>9 Tyler St, Boston, MA 02111</p>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-4">
                  <Phone size={18} className="text-red-600" />
                  <p>(617) 426-4338</p>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Specialties:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Traditional Dim Sum</li>
                    <li>Cart Service</li>
                    <li>Cantonese Cuisine</li>
                    <li>Seafood Dishes</li>
                  </ul>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Must-Try Dishes:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Shrimp Har Gow</li>
                    <li>BBQ Pork Buns</li>
                    <li>Lobster with Ginger & Scallion</li>
                    <li>Egg Custard Tarts</li>
                  </ul>
                </div>
                <div className="flex gap-4 mt-6">
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=9+Tyler+St+Boston+MA+02111`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 border-2 border-red-600 text-red-600 rounded-md hover:bg-red-600 hover:text-white transition-colors text-base font-medium"
                  >
                    Get Directions
                  </a>
                  <a
                    href="tel:6174264338"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors gap-2 text-base font-medium"
                  >
                    <Phone size={18} />
                    Call Now
                  </a>
                </div>
              </div>

              {/* Gourmet Dumpling House */}
              <div className="border-b border-gray-200 pb-8">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-semibold">Gourmet Dumpling House</h3>
                  <div className="flex items-center bg-yellow-100 px-3 py-1.5 rounded-full">
                    <Star size={18} className="text-yellow-500 mr-1" />
                    <span className="font-medium">4.5</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-3">
                  <MapPin size={18} className="text-red-600" />
                  <p>52 Beach St, Boston, MA 02111</p>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-4">
                  <Phone size={18} className="text-red-600" />
                  <p>(617) 338-6223</p>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Specialties:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Soup Dumplings</li>
                    <li>Sichuan Specialties</li>
                    <li>Hand-Made Dumplings</li>
                    <li>Spicy Dishes</li>
                  </ul>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Must-Try Dishes:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Xiao Long Bao</li>
                    <li>Mini Juicy Dumplings</li>
                    <li>Sichuan Style Fish</li>
                    <li>Scallion Pancakes</li>
                  </ul>
                </div>
                <div className="flex gap-4 mt-6">
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=52+Beach+St+Boston+MA+02111`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 border-2 border-red-600 text-red-600 rounded-md hover:bg-red-600 hover:text-white transition-colors text-base font-medium"
                  >
                    Get Directions
                  </a>
                  <a
                    href="tel:6173386223"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors gap-2 text-base font-medium"
                  >
                    <Phone size={18} />
                    Call Now
                  </a>
                </div>
              </div>

              {/* Peach Farm */}
              <div className="border-b border-gray-200 pb-8">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-semibold">Peach Farm</h3>
                  <div className="flex items-center bg-yellow-100 px-3 py-1.5 rounded-full">
                    <Star size={18} className="text-yellow-500 mr-1" />
                    <span className="font-medium">4.4</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-3">
                  <MapPin size={18} className="text-red-600" />
                  <p>4 Tyler St, Boston, MA 02111</p>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-4">
                  <Phone size={18} className="text-red-600" />
                  <p>(617) 482-3332</p>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Specialties:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Live Seafood</li>
                    <li>Late Night Dining</li>
                    <li>Cantonese Style</li>
                    <li>Fresh Fish Tanks</li>
                  </ul>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Must-Try Dishes:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Salt & Pepper Squid</li>
                    <li>Clams in Black Bean Sauce</li>
                    <li>Steamed Fish</li>
                    <li>Lobster with Ginger & Scallion</li>
                  </ul>
                </div>
                <div className="flex gap-4 mt-6">
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=4+Tyler+St+Boston+MA+02111`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 border-2 border-red-600 text-red-600 rounded-md hover:bg-red-600 hover:text-white transition-colors text-base font-medium"
                  >
                    Get Directions
                  </a>
                  <a
                    href="tel:6174823332"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors gap-2 text-base font-medium"
                  >
                    <Phone size={18} />
                    Call Now
                  </a>
                </div>
              </div>

              {/* Q Restaurant */}
              <div className="border-b border-gray-200 pb-8">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-semibold">Q Restaurant</h3>
                  <div className="flex items-center bg-yellow-100 px-3 py-1.5 rounded-full">
                    <Star size={18} className="text-yellow-500 mr-1" />
                    <span className="font-medium">4.6</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-3">
                  <MapPin size={18} className="text-red-600" />
                  <p>660 Washington St, Boston, MA 02111</p>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-4">
                  <Phone size={18} className="text-red-600" />
                  <p>(857) 350-3968</p>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Specialties:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Hot Pot</li>
                    <li>Modern Atmosphere</li>
                    <li>Premium Ingredients</li>
                    <li>All-You-Can-Eat Options</li>
                  </ul>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Must-Try Dishes:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Signature Broths</li>
                    <li>Premium Meat Platters</li>
                    <li>Fresh Seafood Selection</li>
                    <li>House-made Sauces</li>
                  </ul>
                </div>
                <div className="flex gap-4 mt-6">
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=660+Washington+St+Boston+MA+02111`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 border-2 border-red-600 text-red-600 rounded-md hover:bg-red-600 hover:text-white transition-colors text-base font-medium"
                  >
                    Get Directions
                  </a>
                  <a
                    href="tel:8573503968"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors gap-2 text-base font-medium"
                  >
                    <Phone size={18} />
                    Call Now
                  </a>
                </div>
              </div>

              {/* Five Spices House */}
              <div className="border-b border-gray-200 pb-8">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-semibold">Five Spices House</h3>
                  <div className="flex items-center bg-yellow-100 px-3 py-1.5 rounded-full">
                    <Star size={18} className="text-yellow-500 mr-1" />
                    <span className="font-medium">4.4</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-3">
                  <MapPin size={18} className="text-red-600" />
                  <p>58 Beach St, Boston, MA 02111</p>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-4">
                  <Phone size={18} className="text-red-600" />
                  <p>(617) 574-8888</p>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Specialties:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Sichuan Cuisine</li>
                    <li>Spicy Specialties</li>
                    <li>Traditional Flavors</li>
                    <li>House-made Chili Oil</li>
                  </ul>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Must-Try Dishes:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Mapo Tofu</li>
                    <li>Chongqing Spicy Chicken</li>
                    <li>Dan Dan Noodles</li>
                    <li>Twice Cooked Pork</li>
                  </ul>
                </div>
                <div className="flex gap-4 mt-6">
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=58+Beach+St+Boston+MA+02111`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 border-2 border-red-600 text-red-600 rounded-md hover:bg-red-600 hover:text-white transition-colors text-base font-medium"
                  >
                    Get Directions
                  </a>
                  <a
                    href="tel:6175748888"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors gap-2 text-base font-medium"
                  >
                    <Phone size={18} />
                    Call Now
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-8">
            <h2 className="text-2xl font-semibold text-red-800 mb-6">Boston Chinese Food Fun Facts</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="bg-red-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Historical Significance</h3>
                  <p className="text-gray-700">Boston's Chinatown is the third largest in the United States and has been a cultural hub since the 1870s.</p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Cultural Heritage</h3>
                  <p className="text-gray-700">The neighborhood preserves traditional architecture and hosts numerous cultural festivals throughout the year.</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="bg-red-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Culinary Evolution</h3>
                  <p className="text-gray-700">Boston's Chinese food scene has expanded beyond traditional Cantonese to include diverse regional specialties and modern fusion.</p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Local Innovation</h3>
                  <p className="text-gray-700">Many Boston Chinese restaurants incorporate local New England seafood into traditional recipes.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-8">
            <h2 className="text-2xl font-semibold text-red-800 mb-6">Best Times to Visit</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-xl font-semibold mb-3">Peak Hours</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center gap-2">
                    <Clock size={18} className="text-red-600" />
                    Dim Sum: 9 AM - 2 PM (weekends)
                  </li>
                  <li className="flex items-center gap-2">
                    <Clock size={18} className="text-red-600" />
                    Dinner: 5 PM - 9:30 PM
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-3">Special Events</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center gap-2">
                    <Utensils size={18} className="text-red-600" />
                    Lunar New Year Festival (Jan/Feb)
                  </li>
                  <li className="flex items-center gap-2">
                    <Utensils size={18} className="text-red-600" />
                    August Moon Festival
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};